"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import ProductDetailSkeleton from "@/components/product-detail/ProductDetailSkeleton";

/** Pending shorter than this never shows the overlay (no flash on fast navigations). */
export const PRODUCT_NAV_OVERLAY_DELAY_MS = 120;

/**
 * Product detail skeleton over everything below the Navbar while a product
 * navigation is pending (`active`). Shown only after 120ms — a prefetched
 * product page swaps in before that (and `useLinkStatus` skips the pending
 * state entirely for it). Removed when the navigation finishes or is
 * superseded (`active` → false) and on `pagehide`.
 *
 * Fixed below the Navbar (sticky, h-12 / md:h-20, already clears the status
 * bar with pt-safe), so the top edge adds the safe-area inset instead of
 * using pt-safe; the bottom bar inside clears the home indicator.
 */
export default function ProductNavigationOverlay({ active }: { active: boolean }) {
  const t = useTranslations("product");
  const [delayPassed, setDelayPassed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!active) return;
    const timer = window.setTimeout(() => setDelayPassed(true), PRODUCT_NAV_OVERLAY_DELAY_MS);
    const onPageHide = () => setDismissed(true);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pagehide", onPageHide);
      setDelayPassed(false);
      setDismissed(false);
    };
  }, [active]);

  if (!active || !delayPassed || dismissed) return null;

  return createPortal(
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={t("navigationPending")}
      className="fixed inset-x-0 bottom-0 top-[calc(3rem+var(--loc-safe-area-inset-top))] md:top-[calc(5rem+var(--loc-safe-area-inset-top))] z-50 overflow-hidden bg-white"
    >
      <ProductDetailSkeleton />
    </div>,
    document.body,
  );
}
